import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Link,
  Paper,
  InputAdornment, // ✅ Idinagdag para sa Icon sa TextField
} from "@mui/material";

// ✅ Import Icons
import LockResetIcon from '@mui/icons-material/LockReset';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { forgetpassword } from "../../Model/Login";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0); // seconds

  // (Unchanged logic for useEffect, handleRequestReset, handleResendLink, formatCooldown)
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => {
          if (prev === 1) setSubmitted(false);
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Please enter your email.");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const results = await forgetpassword({ email });

      if (results.success === true || results.sucess === true) {
        setSubmitted(true);
        setCooldown(120);
      } else {
        setError(
          results.error?.msg ||
            results.msg ||
            "Failed to send reset link. Please try again."
        );
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendLink = async () => {
    if (cooldown > 0) return;
    setResendLoading(true);
    setError("");

    try {
      const results = await forgetpassword({ email });
      if (results.success || results.sucess) {
        setCooldown(120);
      } else {
        setError(results.msg || "Resend failed. Please try again later.");
      }
    } catch (err) {
      console.error(err);
      setError("Error resending reset link.");
    } finally {
      setResendLoading(false);
    }
  };

  const formatCooldown = () => {
    const minutes = Math.floor(cooldown / 60);
    const seconds = cooldown % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };
  // ----------------------------------------------------------------------


  return (
    <Box
      sx={{
        minHeight: "80vh",
        backgroundImage: "url('/static/bgLogin.jpg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgb(204 202 202 / 45%)",
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 2 }}>
        <Paper
          elevation={6}
          sx={{
            p: 5,
            borderRadius: 3,
            textAlign: "center",
            backdropFilter: "blur(6px)",
            backgroundColor: "rgba(255,255,255,0.95)",
            boxShadow: "0px 8px 30px rgba(0,0,0,0.25)",
          }}
        >
          
          <LockResetIcon 
            sx={{ fontSize: 48, color: "#002452", mb: 1, mt: -1 }} 
          />

          <Typography
            variant="h4"
            fontWeight="bold"
            gutterBottom
            sx={{ color: "#002452" }}
          >
            Forgot Password
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Enter your registered email address below to receive a password reset
            link.
          </Typography>

          {submitted ? (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                A password reset link has been sent to <strong>{email}</strong>.
              </Alert>

              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}

<Button
    onClick={handleResendLink}
    variant="contained"
    fullWidth
    // Para masigurong puti ang icon/text sa enabled state
    startIcon={resendLoading || cooldown > 0 ? null : <SendIcon sx={{ color: 'white' }} />} 
    sx={{
        mt: 3,
        height: 48,
        fontWeight: 600,
        borderRadius: 2,
        // ✅ White color para sa text at icon sa normal state
        color: "white", 
        background: "linear-gradient(90deg, #495057 0%, #212529 100%)",
        "&:hover": {
            backgroundColor: "#343a40",
            transform: "translateY(-1px)",
            boxShadow: "0px 4px 10px rgba(0,0,0,0.3)",
        },
        
        // ✅ FIX: I-override ang disabled state color
        "&.Mui-disabled": {
            // Tiyakin na puti ang text at hindi mag-o-override ang default gray/black
            color: "white !important", 
            // Optional: Maaari mo ring ayusin ang background color ng disabled state
            backgroundColor: "#495057", 
            opacity: 0.7,
        }
    }}
    disabled={resendLoading || cooldown > 0}
>
    {resendLoading ? (
        // CircularProgress ay kailangan ding puti
        <CircularProgress size={24} sx={{color: 'white'}} />
    ) : cooldown > 0 ? (
        // Ito na ang magiging puti
        `Resend in ${formatCooldown()}`
    ) : (
        "Resend Link"
    )}
</Button>
            </>
          ) : (
            <form onSubmit={handleRequestReset}>
              <TextField
                label="Email Address"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                type="email"
                required
                // ✅ ICON: Input Adornment para sa Email Icon
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {error}
                </Alert>
              )}


              <Button
                type="submit"
                fullWidth
                variant="contained"
                // ✅ ICON: Para sa Request Reset
                startIcon={loading ? null : <LockResetIcon />} 
                sx={{
                  mt: 3,
                  height: 48,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#fff",
                  background: "linear-gradient(90deg, #495057 0%, #212529 100%)",
                  boxShadow: "0px 3px 6px rgba(0,0,0,0.2)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#343a40",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.3)",
                    transform: "translateY(-1px)",
                  },
                  
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} style={{color: "#fff",}} />
                ) : (
                  "Request Reset Link"
                )}
              </Button>
            </form>
          )}

          <Link
            href="/login"
            underline="hover"
            sx={{
              display: "flex", // ✅ Ginawang flex para ma-align ang icon
              alignItems: "center",
              justifyContent: "center",
              mt: 4,
              fontSize: 14,
              fontWeight: 500,
              color: "#3e4396",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            
            <ArrowBackIcon sx={{ fontSize: 16, mr: 0.5 }} />
            Back to Login
          </Link>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;